/* ──────────────────────────────────────────────
   src/components/CentroDialog.tsx
   Diálogo estandarizado para crear/editar centros
   ────────────────────────────────────────────── */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Stack,
} from '@mui/material';
import type { DialogProps } from '@mui/material/Dialog';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Centro } from '../hooks/useCentros';
import type { SimpleFormDialogProps } from './dialogs/types';

type Props = SimpleFormDialogProps<Centro, Omit<Centro, 'id'>> &
  Omit<DialogProps, 'open' | 'onClose' | 'children'>;

export default function CentroDialog({
  open,
  initial,
  onClose,
  onSave,
  onDelete,
  readOnly = false,
  ...dialogProps
}: Props) {
  const { t } = useTranslation<'translation'>();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<Omit<Centro, 'id'>>({
    defaultValues: { nombre: '', activo: true },
  });

  useEffect(() => {
    if (initial) {
      reset(initial);
    } else {
      reset({ nombre: '', activo: true });
    }
  }, [initial, reset, open]);

  const activo = watch('activo');

  const submit = handleSubmit(async (data) => {
    if (readOnly) return onClose();

    setSubmitting(true);
    try {
      await onSave(data);
      onClose();
    } catch (err: any) {
      const raw = err?.response?.data?.message;
      const msg = Array.isArray(raw)
        ? raw.join('\n')
        : (raw ?? err?.message ?? t('common.unexpectedError'));
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  });

  const handleDelete = async () => {
    if (!onDelete) return;
    setSubmitting(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      const raw = err?.response?.data?.message;
      const msg = Array.isArray(raw)
        ? raw.join('\n')
        : (raw ?? err?.message ?? t('common.unexpectedError'));
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth {...dialogProps}>
      <DialogTitle>
        {initial
          ? t('centroDialog.editTitle', { defaultValue: 'Editar centro' })
          : t('centroDialog.newTitle', { defaultValue: 'Nuevo centro' })}
      </DialogTitle>

      <form onSubmit={submit}>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={t('centroDialog.name', { defaultValue: 'Nombre' })}
              fullWidth
              disabled={readOnly || submitting}
              error={!!errors.nombre}
              helperText={errors.nombre?.message}
              {...register('nombre', {
                required:
                  t('common.required', { defaultValue: 'Campo requerido' }) ?? 'Campo requerido',
              })}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={activo}
                  disabled={readOnly || submitting}
                  {...register('activo')}
                />
              }
              label={t('centroDialog.active', { defaultValue: 'Activo' })}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          {onDelete && initial && (
            <Button color="error" onClick={handleDelete} disabled={submitting || readOnly}>
              {t('common.delete', { defaultValue: 'Eliminar' })}
            </Button>
          )}
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel', { defaultValue: 'Cancelar' })}
          </Button>
          {!readOnly && (
            <Button variant="contained" type="submit" disabled={submitting}>
              {submitting
                ? t('common.saving', { defaultValue: 'Guardando...' })
                : initial
                  ? t('centroDialog.save', { defaultValue: 'Guardar' })
                  : t('centroDialog.create', { defaultValue: 'Crear' })}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
